import React from 'react'
import LayoutFull from '../../layouts/layoutFull/LayoutFull';
import { createFetchAsync } from "../../helpers/fetch/fetchRequestAsync";
import { JSONLD, SeoCommon, SeoOpenGraph } from "../../helpers/seo/seo";
import { toISO } from "../../helpers/common";
import { API_ARTICLES_EXPLAINS_LIST, generateArticleSchema } from "../../settings/content/articles";
import { createExplainsTree } from "../../pagesComponents/explains/_helpers";
import { Block } from "../../layouts/Content";
import Breadcrumb, { BreadcrumbLayer } from "../../components/Breadcrumb";
import styles from "./[slug].module.scss";
import NavGroup from "../../pagesComponents/explains/NavGroup";
import { URL_BASE } from "../../settings/global";
import DraftHTML from "../../components/draftEditor/export/DraftHTML";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { convertEditorStateToHTML, getPlainTextFromContent } from "../../components/draftEditor/export/exports";
import { NAV_EXPLAINS } from "../../settings/navigation";
import { API_ARTICLES_INTERNAL } from "../../settings/endpoints/app";

export async function getStaticPaths() {

    const res = await fetch(API_ARTICLES_EXPLAINS_LIST);
    const posts = await res.json();
    let articles = posts.response;
    let paths = [];

    articles.map(article => {
        if (!!article.slug) paths.push({params: {slug: article.slug}})
    });

    return {
        paths: paths,
        // paths: [],
        fallback: 'blocking',
    }
}

async function getNextArticle(articles, article) {

    let nextArticle = null;
    if (!article) return null;
    const {_id} = article;
    const groupIndex = articles.findIndex(a => a.docs.some(d => d._id === _id));
    const currentGroupDocs = articles[groupIndex].docs;
    const articleIndex = currentGroupDocs.findIndex(d => d._id === _id);

    if (!!currentGroupDocs[articleIndex + 1]) {
        nextArticle = currentGroupDocs[articleIndex + 1];
    } else if (!!articles[groupIndex + 1]) {
        nextArticle = articles[groupIndex + 1].docs[0]
    }

    return nextArticle
}

export async function getStaticProps({params}) {

    const article = await createFetchAsync({
        URL: `${API_ARTICLES_INTERNAL}/${params.slug}`,
    });

    if (!article.data.response[0]) {
        return {props: {}, notFound: true}
    }

    const articles = await createFetchAsync({
        URL: API_ARTICLES_EXPLAINS_LIST,
    });
    const articlesTree = await createExplainsTree(articles.data.response);

    let _article = article.data.response[0];
    _article.preview_plain = getPlainTextFromContent(_article.preview);
    _article.content_plain = getPlainTextFromContent(_article.content);
    _article.content = convertEditorStateToHTML(_article.content, {styles: DOCUMENTS_STYLES});

    return {
        props: {
            article: article.data.response[0],
            articlesTree,
            nextArticle: await getNextArticle(articlesTree, _article),
            schemaData: generateArticleSchema(_article)
        }
    };
}

const DOCUMENTS_STYLES = {
    'header-one': styles['documentArticle__header-one'],
    'header-two': styles['documentArticle__header-two'],
    'header-three': styles['documentArticle__header-three'],
    'header-four': styles['documentArticle__header-four'],
    'header-five': styles['documentArticle__header-five'],
    'header-six': styles['documentArticle__header-six'],
    'unstyled': styles['documentArticle__unstyled'],
    'paragraph': styles['documentArticle__paragraph'],
    'blockquote': styles['documentArticle__blockquote'],
    'code-block': styles['documentArticle__code-block'],
    'unordered-list-item': styles['documentArticle__unordered-list-item'],
    'ordered-list-item': styles['documentArticle__ordered-list-item'],
}

export default function Article({article, articlesTree, nextArticle, schemaData}) {

    const router = useRouter();

    return (
        <LayoutFull>
            <SeoCommon
                tags={{
                    title: article.title,
                    description: article.description_seo,
                }}
            />
            <SeoOpenGraph
                tags={{
                    title: article.title,
                    description: article.description_seo,
                    type: 'article',
                    custom: {
                        'article:published_time': toISO(article.created),
                        'article:modified_time': toISO(article.updated),
                        'article:author': article.author,
                        'article:tag': article.author,
                    }
                }}
            />
            <JSONLD data={schemaData} tagKey={`article-${article.slug + article.name + article._id}`}/>
            <Head>
                <link rel="canonical" href={`${URL_BASE}${NAV_EXPLAINS}/${router.query.slug}`} key='canon'/>
            </Head>

            <div className={styles.docs}>
                <div className={`${styles.docs__nav}`}>
                    <div className={styles.docs__nav__container}>
                        {
                            articlesTree.map((group, index) => {
                                return (
                                    <NavGroup
                                        group={group}
                                        article={article}
                                        key={index}
                                    />
                                )
                            })
                        }
                    </div>
                </div>
                <div className={styles.docs__container}>
                    <Block>
                        <Breadcrumb className={styles.docs__container__breadcrumb}>
                            <BreadcrumbLayer link='/' name='Home'/>
                            <BreadcrumbLayer link={NAV_EXPLAINS} name='Explains' internal/>
                            <BreadcrumbLayer name={article.title}/>
                        </Breadcrumb>
                    </Block>
                    <Link href={NAV_EXPLAINS} className={styles.docs__container__back}>

                        <i className={`fas fa-chevron-left`}/><p>Back to explains list</p>

                    </Link>
                    <div className={styles.docs__container__content}>
                        <h1 className={styles.title}>{article.title}</h1>
                        {/*<DraftHTML*/}
                        {/*    content={article.content}*/}
                        {/*    className={styles.documentArticle}*/}
                        {/*    styles={DOCUMENTS_STYLES}*/}
                        {/*/>*/}
                        <article>
                            <DraftHTML
                                className={`content-editor blog-article ${styles.documentArticle}`}
                                styles={DOCUMENTS_STYLES}
                                content={article.content}
                                nav={article.config?.nav}
                                navDepth={article.config?.navDepth}
                            />
                        </article>
                        {
                            nextArticle &&
                            <p className={styles.next}>
                                Next article: {' '}
                                <Link href={`${NAV_EXPLAINS}/${nextArticle.slug}`}>
                                    {nextArticle.title || 'Untitled'}
                                </Link>
                            </p>
                        }
                    </div>
                </div>
            </div>
        </LayoutFull>
    );
}